import { createApi } from '@reduxjs/toolkit/query/react';
import B2ChatClient, {
  apiResource,
  b2chatBaseQuery,
  B2ChatClientCalls,
  B2ChatClientCatalog,
} from '@src/client';
import { Call, CallPermission } from '@src/types/calls';
import { DateTypeRange } from '@src/utils/fnsDateUtils';

type CallId = string;

export type CallPermissionResponse = {
  actions: CallPermission[];
};

export type CallPermissionParams = {
  phoneNumberId: string;
  phoneNumber?: string;
  contactBsuid?: string;
};

export type StartCallRequest = {
  chatId: string;
  agent: {
    name: string;
    username: string;
  };
  callContext: {
    call: {
      id: CallId;
      callee: Call['callee'];
      caller: Call['caller'];
      sdpOffer: string;
    };
    sessionId: number | string;
    handleId: number | string;
    roomId?: number;
  };
};

export type RequestOutgoingCallRequest = {
  callContext: {
    call: {
      callee: Call['caller'];
      caller: Call['callee'];
    };
    sessionId: number | string;
    handleId: number | string;
    roomId?: number;
  };
};

export type RequestOutgoingContactCallRequest = {
  botId: number | string;
  viaBotAccount: string;
  callerPhoneNumber: string;
  contact: {
    id: number;
    phoneNumber: string;
    name?: string;
    bsuid?: string;
  };
  callContext: {
    sessionId: number | string;
    handleId: number | string;
    roomId?: number;
  };
};

export type StartOutgoingCallRequest = {
  callId: CallId;
  callContext: {
    roomId?: number;
    call: {
      callee: Call['caller'];
      caller: Call['callee'];
      sdpAnswer: string;
      sdpOfferHandleId: number;
    };
  };
};

export type EndCallRequest = {
  callId: CallId;
  whatsappPhoneNumberId: string;
  chatId: string;
  contactPhoneNumber: string;
  contactBsuid?: string;
  mode?: string;
};

export type GetAllCallsRequest = {
  initiatedAt?: DateTypeRange;
  contact?: string;
  direction?: string;
  status?: string;
  source?: string;
  departmentId?: string;
  agentUsername?: string;
  merchantPhoneNumber?: string;
  limit: number;
  offset: number;
};

export type GetAllCallsResponse = {
  data: Call[];
  paging: {
    hasMore: boolean;
    total: number;
  };
};

export type GetCanCallRequest = { lineId: number | string };
export type GetCanCallResponse = { canCall: boolean };

const PHONE_NUMBER_LIKE = /^\+?\d[\d ]+$/;

const callsApi = createApi({
  reducerPath: 'callsApi',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['calls', 'permissions'],
  endpoints: ({ query, mutation }) => ({
    getAllCalls: query<GetAllCallsResponse, GetAllCallsRequest>({
      providesTags: ['calls'],
      query: data => {
        if (data.contact && PHONE_NUMBER_LIKE.test(data.contact)) {
          data.contact = data.contact.replace(/\s/g, '');
        }
        return apiResource(
          B2ChatClientCatalog.resources.calls.actions.getAllCalls,
          {
            params: {
              'initiatedAt[gte]': data.initiatedAt?.[0]?.toISOString() || '',
              'initiatedAt[lte]': data.initiatedAt?.[1]?.toISOString() || '',
              'contact[like]': data.contact || '',
              direction: data.direction || '',
              status: data.status || '',
              source: data.source || '',
              departmentId: `${data.departmentId || ''}`,
              agentUsername: data.agentUsername || '',
              merchantPhoneNumber: data.merchantPhoneNumber || '',
              limit: `${data.limit}`,
              offset: `${data.offset}`,
            },
            withJWT: true,
            transformError() {
              return {
                data: { data: [], paging: { hasMore: false, total: 0 } },
              };
            },
          }
        );
      },
      extraOptions: { maxRetries: 1 },
    }),
    getCanCall: query<GetCanCallResponse, GetCanCallRequest>({
      providesTags: (_result, _error, { lineId }) => [
        { type: 'permissions', id: `line-${lineId}` },
      ],
      extraOptions: { maxRetries: 0 },
      keepUnusedDataFor: 60,
      query: ({ lineId }) =>
        apiResource(B2ChatClient.resources.calling.actions.canCall, {
          params: { botAccountId: String(lineId) },
          withJWT: true,
        }),
    }),
    getCallPermission: query<CallPermission[], CallPermissionParams>({
      providesTags: (
        _result,
        _error,
        { phoneNumberId, phoneNumber, contactBsuid }
      ) => [
        { type: 'permissions', id: phoneNumberId },
        { type: 'permissions', id: phoneNumber ?? contactBsuid },
        'calls',
      ],
      keepUnusedDataFor: 10,
      extraOptions: { maxRetries: 0 },
      query: ({ phoneNumberId, phoneNumber, contactBsuid }) =>
        apiResource(
          B2ChatClientCalls.resources.calls.actions.getCallPermissions,
          {
            params: {
              phoneNumberId,
              ...(phoneNumber && { contactPhoneNumber: phoneNumber }),
              ...(contactBsuid && { contactBsuid }),
            },
            withJWT: true,
          }
        ),
      transformResponse: (response: CallPermissionResponse) => response.actions,
    }),

    // START INCOMING CALL
    requestStartCall: mutation<void, StartCallRequest>({
      invalidatesTags: ['calls'],
      query: data =>
        apiResource(B2ChatClientCalls.resources.calls.actions.startCall, {
          data,
          withJWT: true,
        }),
    }),

    // OUTGOING CALL - SERVICE 1
    requestOutgoingCall: mutation<
      { sdpOfferHandleId: number; roomId?: number },
      RequestOutgoingCallRequest
    >({
      invalidatesTags: ['calls'],
      extraOptions: { maxRetries: 0 },
      query: data =>
        apiResource(
          B2ChatClientCalls.resources.calls.actions.requestOutgoingCall,
          {
            data,
            withJWT: true,
          }
        ),
      // Persist roomId in response data so callEventsListener can read it for
      // the PATCH — RTK Query drops originalArgs when fixedCacheKey is used.
      transformResponse: (
        response: { sdpOfferHandleId: number },
        _meta,
        arg
      ) => ({ ...response, roomId: arg.callContext.roomId }),
    }),

    // OUTGOING CALL(BY CONTACT) - SERVICE 1
    requestOutgoingContactCall: mutation<
      { sdpOfferHandleId: number; roomId?: number },
      RequestOutgoingContactCallRequest
    >({
      invalidatesTags: ['calls'],
      extraOptions: { maxRetries: 0 },
      query: ({ botId, viaBotAccount, ...data }) => {
        const requestId = `${String(botId)}.${String(
          viaBotAccount
        )}.${Date.now()}`;
        return apiResource(
          B2ChatClient.resources.calls.actions.requestOutgoingContactCall,
          {
            data: { ...data, requestId },
          }
        );
      },
      // Same as requestOutgoingCall: persist roomId so the PATCH can read it.
      transformResponse: (
        response: { sdpOfferHandleId: number },
        _meta,
        arg
      ) => ({ ...response, roomId: arg.callContext.roomId }),
    }),

    // OUTGOING CALL(START) - SERVICE 2
    requestStartOutgoingCall: mutation<void, StartOutgoingCallRequest>({
      invalidatesTags: ['calls'],
      extraOptions: { maxRetries: 1 },
      query: data =>
        apiResource(
          B2ChatClientCalls.resources.calls.actions.startOutgoingCall,
          {
            params: { id: data.callId },
            data: { callContext: data.callContext },
            withJWT: true,
          }
        ),
    }),

    // END OUTGOING | INCOMING CALLS
    requestEndCall: mutation<void, EndCallRequest>({
      invalidatesTags: ['calls'],
      query: ({ callId, mode, ...data }) => {
        const params: { callId: string; mode?: string } = {
          callId,
          ...(mode && { mode }),
        };
        return apiResource(B2ChatClientCalls.resources.calls.actions.endCall, {
          params,
          data,
          withJWT: true,
        });
      },
    }),
  }),
});

export const {
  useGetAllCallsQuery,
  useLazyGetAllCallsQuery,
  useRequestOutgoingCallMutation,
  useRequestStartOutgoingCallMutation,
  useRequestStartCallMutation,
  useRequestEndCallMutation,
  useGetCallPermissionQuery,
  useLazyGetCallPermissionQuery,
  useRequestOutgoingContactCallMutation,
  useGetCanCallQuery,
  useLazyGetCanCallQuery,
} = callsApi;

export default callsApi;
